import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { backdropUrl } from "../api/images";
import { ContentType } from "../api/tmdb";
import { GENRE_BROWSE } from "../api/tmdb";
import { yearOf } from "../lib/format";
import { useWatchlist, toggleWatchlist, watchlistItemFromCard, isInWatchlist } from "../storage/watchlist";
import { getProgress } from "../storage/progress";

const ROTATE_MS = 7000;

export default function HeroCarousel({ slides }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = slides.length;
  const current = count > 0 ? slides[index % count] : null;

  const play = (movie) => {
    if (!movie) return;
    const saved = getProgress(ContentType.MOVIE, movie.tmdbId);
    navigate(
      `/watch?type=movie&id=${movie.tmdbId}&title=${encodeURIComponent(movie.title)}&poster=${encodeURIComponent(movie.posterPath || "")}&backdrop=${encodeURIComponent(movie.backdropPath || "")}&lang=${encodeURIComponent(movie.originalLanguage || "")}&resume=${saved?.positionSeconds || 0}`,
    );
  };

  const go = (delta) => {
    if (count > 0) setIndex((i) => (i + delta + count) % count);
  };

  // Auto-rotate; pauses while hovered or when the tab is hidden so the user
  // never loses their place.
  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  useEffect(() => {
    const onVisibility = () => setPaused(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (!current) return null;

  const year = yearOf(current.date);
  const inList = isInWatchlist(current.contentType, current.tmdbId);
  const genreNames = (current.genreIds || [])
    .map((id) => GENRE_BROWSE.find((g) => g.genreId === String(id))?.title)
    .filter(Boolean);

  return (
    <section
      className={`hero hero-carousel${count > 1 ? " has-many" : ""}`}
      aria-roledescription="carousel"
      aria-label="Recent OTT releases"
      onClick={() => play(current)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Backgrounds — all mounted so images preload; the active one fades in. */}
      {slides.map((movie, i) => {
        const bg = backdropUrl(movie.backdropPath, { hero: true });
        const isActive = i === index % count;
        return (
          <div key={movie.tmdbId} className={`hero-slide${isActive ? " active" : ""}`} aria-hidden={!isActive}>
            {bg ? <img className="hero-backdrop" src={bg} alt="" /> : null}
          </div>
        );
      })}
      <div className="hero-scrim" />

      <div className="hero-content" key={index % count}>
        <h1 className="hero-title">{current.title}</h1>
        <p className="hero-meta">
          {year && <span className="hero-meta-item">{year}</span>}
          {(genreNames || []).slice(0, 3).map((g) => (
            <span key={g} className="hero-meta-item">{g}</span>
          ))}
        </p>
        {current.overview && <p className="hero-overview">{current.overview}</p>}
        <div className="hero-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-play" onClick={() => play(current)}>
            ▶ Play
          </button>
          <button
            className="btn btn-more"
            onClick={() => navigate(`/movie/${current.tmdbId}`)}
          >
            ⓘ See More
          </button>
          <button
            className={`btn btn-ghost${inList ? " in-list" : ""}`}
            onClick={() => toggleWatchlist(watchlistItemFromCard(current))}
            aria-label={inList ? "Remove from My List" : "Add to My List"}
          >
            {inList ? "✓" : "+"}
          </button>
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            className="hero-arrow prev"
            aria-label="Previous slide"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
          >
            ‹
          </button>
          <button
            className="hero-arrow next"
            aria-label="Next slide"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
          >
            ›
          </button>
          <div className="hero-dots" onClick={(e) => e.stopPropagation()}>
            {slides.map((movie, i) => (
              <button
                key={movie.tmdbId}
                className={`hero-dot${i === index % count ? " active" : ""}`}
                aria-label={`Go to slide ${i + 1}: ${movie.title}`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
